
import React, { useState, useContext } from 'react';
import { Smartphone } from 'lucide-react';
import FallbackSVGPhone from './FallbackSVGPhone';
import { AppContext } from '../context/AppContext';

interface ModelImageProps {
  id?: string;
  type?: 'model' | 'brand' | 'part' | 'blog' | 'product';
  src?: string;
  alt?: string;
  title?: string;
  modelName?: string;
  className?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  loading?: "lazy" | "eager";
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  model?: {
    id?: string | number;
    source?: 'inventory' | 'repair';
    image?: string;
    image_url?: string;
    name?: string;
  };
  noPadding?: boolean;
  presentation?: 'default' | 'catalog' | 'hero' | 'summary';
  optimizeWidth?: number;
}

const ModelImage: React.FC<ModelImageProps> = ({ 
  id,
  type,
  src: originalSrc, 
  alt: providedAlt, 
  title: providedTitle, 
  modelName,
  className = "", 
  referrerPolicy = "no-referrer",
  loading = "lazy",
  width,
  height,
  style,
  model,
  noPadding = false,
  presentation = 'default',
  optimizeWidth
}) => {
  const context = useContext(AppContext);
  const storeName = context?.footerInfo?.siteName || context?.footerInfo?.nom_du_magasin || 'Mobilbo';

  // Resolve props optionally from 'model' object to keep pages robust
  const resolvedId = id || model?.id;
  const resolvedType = type || (model?.source === 'inventory' ? 'product' : 'model') || 'model';
  const resolvedSrc = originalSrc || model?.image || model?.image_url;
  const resolvedModelName = modelName || model?.name;

  // Normalize upload paths starting with uploads/ or ./uploads/
  let normalizedSrc = resolvedSrc || '';
  if (normalizedSrc.startsWith('uploads/') || normalizedSrc.startsWith('./uploads/')) {
    normalizedSrc = '/' + normalizedSrc.replace(/^\.\//, '');
  }

  // Use proxy URL if id and type are provided, BUT bypass if it's explicitly a base64, external url, /uploads/ or /assets/ path
  const baseSrc = (normalizedSrc && (normalizedSrc.startsWith('http') || normalizedSrc.startsWith('data:') || normalizedSrc.startsWith('/uploads/') || normalizedSrc.startsWith('/assets/'))) 
    ? normalizedSrc 
    : (resolvedId && resolvedType && normalizedSrc && normalizedSrc !== "") ? `/api/media/${resolvedType}/${encodeURIComponent(resolvedId)}` : normalizedSrc;
  const optimizedSrc = optimizeWidth && /^\/uploads\/images\/[a-zA-Z0-9._-]+$/.test(normalizedSrc)
    ? `/api/public/media-thumbnail?src=${encodeURIComponent(normalizedSrc)}&width=${Math.max(64, Math.min(640, Math.round(optimizeWidth)))}`
    : '';
  const [optimizedFailed, setOptimizedFailed] = useState(false);
  const src = optimizedSrc && !optimizedFailed ? optimizedSrc : baseSrc;
  
  // SEO Logic: Apple-style rich printing
  const finalAlt = providedAlt || (resolvedModelName ? `Détail technique et design du ${resolvedModelName}. Certifié pour réparation à votre région chez ${storeName}.` : `Image de produit high-tech ${storeName}`);
  const finalTitle = providedTitle || (resolvedModelName ? `Réparation ${resolvedModelName} votre région - Excellence & Précision` : `${storeName} - Expert Réparation`);

  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [prevSrc, setPrevSrc] = useState(src);

  if (src !== prevSrc) {
    setPrevSrc(src);
    setError(false);
    setIsLoading(!!src);
  }

  if (error || !src || src.trim() === '') {
    if (resolvedType === 'brand') {
      return (
        <span className={`text-white font-black tracking-widest uppercase flex items-center justify-center h-full ${(className || '').replace('brightness-0 invert', '')}`}>
          {resolvedModelName || "MARQUE"}
        </span>
      );
    }
    
    // Instead of the grey box, we render the beautiful full-screen fallback SVG!
    // Extract a possible brand name from modelName if none was explicitly given
    let brandGuess = 'Smartphone';
    if (resolvedModelName) {
        if (resolvedModelName.toLowerCase().includes('iphone') || resolvedModelName.toLowerCase().includes('apple')) brandGuess = 'Apple';
        else if (resolvedModelName.toLowerCase().includes('samsung') || resolvedModelName.toLowerCase().includes('galaxy')) brandGuess = 'Samsung';
        else if (resolvedModelName.toLowerCase().includes('xiaomi') || resolvedModelName.toLowerCase().includes('redmi') || resolvedModelName.toLowerCase().includes('poco')) brandGuess = 'Xiaomi';
        else if (resolvedModelName.toLowerCase().includes('huawei') || resolvedModelName.toLowerCase().includes('honor')) brandGuess = 'Huawei';
        else if (resolvedModelName.toLowerCase().includes('oppo')) brandGuess = 'Oppo';
        else if (resolvedModelName.toLowerCase().includes('oneplus')) brandGuess = 'OnePlus';
        else if (resolvedModelName.toLowerCase().includes('nokia')) brandGuess = 'Nokia';
        else if (resolvedModelName.toLowerCase().includes('sony')) brandGuess = 'Sony';
        else if (resolvedModelName.toLowerCase().includes('google') || resolvedModelName.toLowerCase().includes('pixel')) brandGuess = 'Google';
    }

    return (
      <div className={`relative flex items-center justify-center w-full h-full p-3 md:p-4 ${className}`}>
        <FallbackSVGPhone modelName={resolvedModelName || 'Produit'} brandName={brandGuess} />
      </div>
    );
  }

  const paddingClass = (resolvedType === 'model' && !noPadding) ? 'p-1 md:p-2' : '';
  const presentationScale = {
    default: 'scale-100',
    catalog: 'scale-110',
    hero: 'scale-[1.18]',
    // The summary sits inside a rounded thumbnail. Keep the complete device
    // inside its safe area instead of enlarging it beyond the clipped bounds.
    summary: 'scale-100'
  }[presentation];
  const presentationInset = presentation === 'summary' ? 'p-2 sm:p-2.5' : '';

  return (
    <div data-model-image-presentation={presentation} className={`relative flex h-full w-full items-center justify-center overflow-hidden ${paddingClass} ${presentationInset} ${className}`} style={style}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 animate-pulse rounded-2xl z-0">
          <Smartphone className="w-8 h-8 text-gray-200" />
        </div>
      )}
      <img
        src={src}
        alt={finalAlt}
        title={finalTitle}
        loading={loading}
        referrerPolicy={referrerPolicy}
        width={width}
        height={height}
        className={`relative z-10 h-full w-full max-h-full max-w-full object-contain transition-all duration-500 ${isLoading ? 'opacity-0 scale-95' : `opacity-100 ${presentationScale}`}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          if (optimizedSrc && !optimizedFailed) {
            setOptimizedFailed(true);
            setIsLoading(true);
            return;
          }
          setError(true);
          setIsLoading(false);
        }}
      />
    </div>
  );
};

export default ModelImage;
